import * as z from 'zod';

export const CreateBackupRequest = z.object({});
export const CreateBackupResponse = z.object({
  backup: z
    .object({
      _id: z
        .string()
        .describe('Backup ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      status: z
        .enum(['PENDING', 'READY', 'FAILED', 'DELETED', 'CANCELED'])
        .describe('Backup status.')
        .optional(),
      type: z
        .enum(['ON_DEMAND', 'AUTO'])
        .describe('Type of backup, based on how it was triggered.')
        .optional(),
      requestedDate: z
        .date()
        .describe('Date and time the backup was requested.')
        .optional()
        .nullable(),
      startedDate: z
        .date()
        .describe(
          'Date and time the backup commenced. Value is `null` until the backup process begins in the background.'
        )
        .optional()
        .nullable(),
      finishedDate: z
        .date()
        .describe(
          'Date and time the backup process finished. Value is `null` until the backup process is completed in the background.'
        )
        .optional()
        .nullable(),
      deletedDate: z
        .date()
        .describe(
          "Date and time the backup was deleted. Value is `null` if that backup hasn't been deleted."
        )
        .optional()
        .nullable(),
      sizeInBytes: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Backup size in bytes. Value is `null` until the backup process is completed.'
        )
        .optional()
        .nullable(),
      collections: z
        .array(
          z.object({
            _id: z.string().describe('Collection ID.').max(255).optional(),
            displayName: z
              .string()
              .describe('Collection display name.')
              .max(1000)
              .optional()
              .nullable(),
          })
        )
        .max(1000)
        .optional(),
    })
    .describe('Details of the requested backup.')
    .optional(),
});
export const ListBackupsRequest = z.object({
  options: z
    .object({
      status: z
        .array(z.enum(['PENDING', 'READY', 'FAILED', 'DELETED', 'CANCELED']))
        .max(10)
        .optional(),
      type: z
        .array(z.enum(['ON_DEMAND', 'AUTO']))
        .max(10)
        .optional(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe('Number of items to skip in the current sort order.')
            .min(0)
            .optional()
            .nullable(),
        })
        .describe('Paging preferences.')
        .optional(),
    })
    .optional(),
});
export const ListBackupsResponse = z.object({
  backups: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Backup ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        status: z
          .enum(['PENDING', 'READY', 'FAILED', 'DELETED', 'CANCELED'])
          .describe('Backup status.')
          .optional(),
        type: z
          .enum(['ON_DEMAND', 'AUTO'])
          .describe('Type of backup, based on how it was triggered.')
          .optional(),
        requestedDate: z
          .date()
          .describe('Date and time the backup was requested.')
          .optional()
          .nullable(),
        startedDate: z
          .date()
          .describe(
            'Date and time the backup commenced. Value is `null` until the backup process begins in the background.'
          )
          .optional()
          .nullable(),
        finishedDate: z
          .date()
          .describe(
            'Date and time the backup process finished. Value is `null` until the backup process is completed in the background.'
          )
          .optional()
          .nullable(),
        deletedDate: z
          .date()
          .describe(
            "Date and time the backup was deleted. Value is `null` if that backup hasn't been deleted."
          )
          .optional()
          .nullable(),
        sizeInBytes: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Backup size in bytes. Value is `null` until the backup process is completed.'
          )
          .optional()
          .nullable(),
        collections: z
          .array(
            z.object({
              _id: z.string().describe('Collection ID.').max(255).optional(),
              displayName: z
                .string()
                .describe('Collection display name.')
                .max(1000)
                .optional()
                .nullable(),
            })
          )
          .max(1000)
          .optional(),
      })
    )
    .max(1000)
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging information.')
    .optional(),
});
export const RestoreBackupRequest = z.object({
  backupId: z
    .string()
    .describe('ID of backup to be restored.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const RestoreBackupResponse = z.object({
  restoration: z
    .object({
      _id: z
        .string()
        .describe('Restoration ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      backup: z
        .object({
          _id: z
            .string()
            .describe('Backup ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          status: z
            .enum(['PENDING', 'READY', 'FAILED', 'DELETED', 'CANCELED'])
            .describe('Backup status.')
            .optional(),
          type: z
            .enum(['ON_DEMAND', 'AUTO'])
            .describe('Type of backup, based on how it was triggered.')
            .optional(),
          requestedDate: z
            .date()
            .describe('Date and time the backup was requested.')
            .optional()
            .nullable(),
          startedDate: z
            .date()
            .describe(
              'Date and time the backup commenced. Value is `null` until the backup process begins in the background.'
            )
            .optional()
            .nullable(),
          finishedDate: z
            .date()
            .describe(
              'Date and time the backup process finished. Value is `null` until the backup process is completed in the background.'
            )
            .optional()
            .nullable(),
          deletedDate: z
            .date()
            .describe(
              "Date and time the backup was deleted. Value is `null` if that backup hasn't been deleted."
            )
            .optional()
            .nullable(),
          sizeInBytes: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Backup size in bytes. Value is `null` until the backup process is completed.'
            )
            .optional()
            .nullable(),
          collections: z
            .array(
              z.object({
                _id: z.string().describe('Collection ID.').max(255).optional(),
                displayName: z
                  .string()
                  .describe('Collection display name.')
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .max(1000)
            .optional(),
        })
        .describe('Details of the backup used for the restoration.')
        .optional(),
      status: z
        .enum(['PENDING', 'COMPLETED', 'FAILED'])
        .describe('Status of restoration.')
        .optional(),
      requestedDate: z
        .date()
        .describe('Date and time the restoration was requested.')
        .optional()
        .nullable(),
      startedDate: z
        .date()
        .describe(
          'Date and time the restoration commenced. Value is `null` until the restoration process begins in the background.'
        )
        .optional()
        .nullable(),
      finishedDate: z
        .date()
        .describe(
          'Date and time the restoration finished. Value is `null` until the restoration process is completed in the background.'
        )
        .optional()
        .nullable(),
      restorationCollections: z
        .array(
          z.object({
            dataCollectionId: z
              .string()
              .describe(
                'Collections to be restored.\n\nNote: If collections have a multi-reference relationship,\nthe preexisting references will be restored if at least one of those collections are restored.'
              )
              .min(1)
              .max(1000)
              .optional(),
            restoreDestination: z
              .object({
                dataCollectionId: z
                  .string()
                  .describe('Collection ID.')
                  .min(1)
                  .max(255)
                  .optional(),
                displayName: z
                  .string()
                  .describe(
                    "Collection's display name as shown in the CMS. If empty, `displayName` is taken from `backup.collections`."
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Destination where to restore the collection.\nWhen not specified destination is taken from backup.'
              )
              .optional(),
          })
        )
        .max(1000)
        .optional(),
    })
    .describe('Details of data restoration from backup.')
    .optional(),
});
export const RestorePartialBackupRequest = z.object({
  backupId: z
    .string()
    .describe('ID of backup to be restored.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  restorationCollections: z
    .array(
      z.object({
        dataCollectionId: z
          .string()
          .describe(
            'Collections to be restored.\n\nNote: If collections have a multi-reference relationship,\nthe preexisting references will be restored if at least one of those collections are restored.'
          )
          .min(1)
          .max(1000)
          .optional(),
        restoreDestination: z
          .object({
            dataCollectionId: z
              .string()
              .describe('Collection ID.')
              .min(1)
              .max(255)
              .optional(),
            displayName: z
              .string()
              .describe(
                "Collection's display name as shown in the CMS. If empty, `displayName` is taken from `backup.collections`."
              )
              .max(1000)
              .optional()
              .nullable(),
          })
          .describe(
            'Destination where to restore the collection.\nWhen not specified destination is taken from backup.'
          )
          .optional(),
      })
    )
    .max(1000),
});
export const RestorePartialBackupResponse = z.object({
  restoration: z
    .object({
      _id: z
        .string()
        .describe('Restoration ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional(),
      backup: z
        .object({
          _id: z
            .string()
            .describe('Backup ID.')
            .regex(
              /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
              'Must be a valid GUID'
            )
            .optional(),
          status: z
            .enum(['PENDING', 'READY', 'FAILED', 'DELETED', 'CANCELED'])
            .describe('Backup status.')
            .optional(),
          type: z
            .enum(['ON_DEMAND', 'AUTO'])
            .describe('Type of backup, based on how it was triggered.')
            .optional(),
          requestedDate: z
            .date()
            .describe('Date and time the backup was requested.')
            .optional()
            .nullable(),
          startedDate: z
            .date()
            .describe(
              'Date and time the backup commenced. Value is `null` until the backup process begins in the background.'
            )
            .optional()
            .nullable(),
          finishedDate: z
            .date()
            .describe(
              'Date and time the backup process finished. Value is `null` until the backup process is completed in the background.'
            )
            .optional()
            .nullable(),
          deletedDate: z
            .date()
            .describe(
              "Date and time the backup was deleted. Value is `null` if that backup hasn't been deleted."
            )
            .optional()
            .nullable(),
          sizeInBytes: z
            .string()
            .regex(/^-?\d+$/, 'Must be a valid Int64 string')
            .describe(
              'Backup size in bytes. Value is `null` until the backup process is completed.'
            )
            .optional()
            .nullable(),
          collections: z
            .array(
              z.object({
                _id: z.string().describe('Collection ID.').max(255).optional(),
                displayName: z
                  .string()
                  .describe('Collection display name.')
                  .max(1000)
                  .optional()
                  .nullable(),
              })
            )
            .max(1000)
            .optional(),
        })
        .describe('Details of the backup used for the restoration.')
        .optional(),
      status: z
        .enum(['PENDING', 'COMPLETED', 'FAILED'])
        .describe('Status of restoration.')
        .optional(),
      requestedDate: z
        .date()
        .describe('Date and time the restoration was requested.')
        .optional()
        .nullable(),
      startedDate: z
        .date()
        .describe(
          'Date and time the restoration commenced. Value is `null` until the restoration process begins in the background.'
        )
        .optional()
        .nullable(),
      finishedDate: z
        .date()
        .describe(
          'Date and time the restoration finished. Value is `null` until the restoration process is completed in the background.'
        )
        .optional()
        .nullable(),
      restorationCollections: z
        .array(
          z.object({
            dataCollectionId: z
              .string()
              .describe(
                'Collections to be restored.\n\nNote: If collections have a multi-reference relationship,\nthe preexisting references will be restored if at least one of those collections are restored.'
              )
              .min(1)
              .max(1000)
              .optional(),
            restoreDestination: z
              .object({
                dataCollectionId: z
                  .string()
                  .describe('Collection ID.')
                  .min(1)
                  .max(255)
                  .optional(),
                displayName: z
                  .string()
                  .describe(
                    "Collection's display name as shown in the CMS. If empty, `displayName` is taken from `backup.collections`."
                  )
                  .max(1000)
                  .optional()
                  .nullable(),
              })
              .describe(
                'Destination where to restore the collection.\nWhen not specified destination is taken from backup.'
              )
              .optional(),
          })
        )
        .max(1000)
        .optional(),
    })
    .describe('Details of data restoration from backup.')
    .optional(),
});
export const ListRestorationsRequest = z.object({
  options: z
    .object({
      status: z
        .array(z.enum(['PENDING', 'COMPLETED', 'FAILED']))
        .max(10)
        .optional(),
      paging: z
        .object({
          limit: z
            .number()
            .int()
            .describe('Number of items to load.')
            .min(0)
            .optional()
            .nullable(),
          offset: z
            .number()
            .int()
            .describe('Number of items to skip in the current sort order.')
            .min(0)
            .optional()
            .nullable(),
        })
        .describe('Offset and limit of items to retrieve.')
        .optional(),
    })
    .optional(),
});
export const ListRestorationsResponse = z.object({
  restorations: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Restoration ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional(),
        backup: z
          .object({
            _id: z
              .string()
              .describe('Backup ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional(),
            status: z
              .enum(['PENDING', 'READY', 'FAILED', 'DELETED', 'CANCELED'])
              .describe('Backup status.')
              .optional(),
            type: z
              .enum(['ON_DEMAND', 'AUTO'])
              .describe('Type of backup, based on how it was triggered.')
              .optional(),
            requestedDate: z
              .date()
              .describe('Date and time the backup was requested.')
              .optional()
              .nullable(),
            startedDate: z
              .date()
              .describe(
                'Date and time the backup commenced. Value is `null` until the backup process begins in the background.'
              )
              .optional()
              .nullable(),
            finishedDate: z
              .date()
              .describe(
                'Date and time the backup process finished. Value is `null` until the backup process is completed in the background.'
              )
              .optional()
              .nullable(),
            deletedDate: z
              .date()
              .describe(
                "Date and time the backup was deleted. Value is `null` if that backup hasn't been deleted."
              )
              .optional()
              .nullable(),
            sizeInBytes: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Backup size in bytes. Value is `null` until the backup process is completed.'
              )
              .optional()
              .nullable(),
            collections: z
              .array(
                z.object({
                  _id: z
                    .string()
                    .describe('Collection ID.')
                    .max(255)
                    .optional(),
                  displayName: z
                    .string()
                    .describe('Collection display name.')
                    .max(1000)
                    .optional()
                    .nullable(),
                })
              )
              .max(1000)
              .optional(),
          })
          .describe('Details of the backup used for the restoration.')
          .optional(),
        status: z
          .enum(['PENDING', 'COMPLETED', 'FAILED'])
          .describe('Status of restoration.')
          .optional(),
        requestedDate: z
          .date()
          .describe('Date and time the restoration was requested.')
          .optional()
          .nullable(),
        startedDate: z
          .date()
          .describe(
            'Date and time the restoration commenced. Value is `null` until the restoration process begins in the background.'
          )
          .optional()
          .nullable(),
        finishedDate: z
          .date()
          .describe(
            'Date and time the restoration finished. Value is `null` until the restoration process is completed in the background.'
          )
          .optional()
          .nullable(),
        restorationCollections: z
          .array(
            z.object({
              dataCollectionId: z
                .string()
                .describe(
                  'Collections to be restored.\n\nNote: If collections have a multi-reference relationship,\nthe preexisting references will be restored if at least one of those collections are restored.'
                )
                .min(1)
                .max(1000)
                .optional(),
              restoreDestination: z
                .object({
                  dataCollectionId: z
                    .string()
                    .describe('Collection ID.')
                    .min(1)
                    .max(255)
                    .optional(),
                  displayName: z
                    .string()
                    .describe(
                      "Collection's display name as shown in the CMS. If empty, `displayName` is taken from `backup.collections`."
                    )
                    .max(1000)
                    .optional()
                    .nullable(),
                })
                .describe(
                  'Destination where to restore the collection.\nWhen not specified destination is taken from backup.'
                )
                .optional(),
            })
          )
          .max(1000)
          .optional(),
      })
    )
    .max(1000)
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      offset: z
        .number()
        .int()
        .describe('Offset that was requested.')
        .optional()
        .nullable(),
      total: z
        .number()
        .int()
        .describe(
          'Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set.'
        )
        .optional()
        .nullable(),
      tooManyToCount: z
        .boolean()
        .describe(
          'Flag that indicates the server failed to calculate the `total` field.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging information.')
    .optional(),
});
export const DeleteBackupRequest = z.object({
  backupId: z
    .string()
    .describe('ID of the backup to be deleted.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteBackupResponse = z.object({});
